import {useEffect, useState} from 'react';

import {Grid, TextField} from '@mui/material';
import Box from '@mui/material/Box';
import {arrayOf, bool, func, string} from 'prop-types';

import {ADD_USER_FORMS_IDS, REGULAR_EXPRESSIONS} from '../../const';
import usePayment from '../../hooks/providers/usePayment';
import useProfile from '../../hooks/providers/useProfile';
import {userShape} from '../../proptypes-shapes';
import Button from '../form/buttons/Button/Button';
import Checkbox from '../form/Checkbox/Checkbox';
import TitleWithTooltip from '../project/TitleWithTooltip';

const AddUserForm = ({users, addUser, isSubmitting, emailError, setEmailError, formId}) => {
  const {profile} = useProfile();
  const {isAdmin} = usePayment();
  const [mail, setMail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [shouldSendInvitationEmail, setShouldSendInvitationEmail] = useState(false);

  const checkEmailAddress = email => {
    if (profile.mail === email && !isAdmin) {
      setIsEmailValid(false);
      setEmailError('Vous ne pouvez vous inviter vous-même');
      return;
    }

    if (users.find(u => u.mail === email) !== undefined) {
      setIsEmailValid(false);
      setEmailError('Vous ne pouvez ajouter 2 fois le même utilisateur');
      return;
    }

    const isValid = REGULAR_EXPRESSIONS.email.test(email);

    setIsEmailValid(isValid);
    if (!isValid) {
      setEmailError('Adresse email invalide');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async () => {
    const res = await addUser(mail.toLowerCase(), shouldSendInvitationEmail);
    if (res.status === 200 || res.status === 402) {
      setMail('');
    }
  };

  useEffect(() => {
    checkEmailAddress(mail);
  }, [mail]);

  const isWorkspaceOrReportForm = [ADD_USER_FORMS_IDS.WORKSPACE, ADD_USER_FORMS_IDS.REPORT].includes(formId);

  const tooltipTitle = isWorkspaceOrReportForm ? 'Ajouter un utilisateur' : 'Ajouter un invité';

  const getTooltip = () => {
    if (formId === ADD_USER_FORMS_IDS.CONNECTION) {
      return "L'invité devra créer un compte Drivn pour accéder à la connexion";
    }
    if (formId === ADD_USER_FORMS_IDS.WORKSPACE) {
      return "L'utilisateur aura les mêmes droits propriétaires que vous";
    }
    if (formId === ADD_USER_FORMS_IDS.REPORT) {
      return "L'utilisateur aura des droits de lecture seulement";
    }

    return '';
  };
  const tooltip = getTooltip();

  return (
    <Grid container alignItems="flex-start" spacing={1} mb={5}>
      <Grid item xs={12} container alignItems="center" mb={1}>
        <TitleWithTooltip title={tooltipTitle} tooltip={tooltip} />
      </Grid>
      <Grid item xs={12} sm={10}>
        <TextField
          error={!isEmailValid && mail !== ''}
          size="small"
          value={mail}
          label="Renseignez une adresse email"
          color="primary"
          placeholder="Renseignez une adresse email"
          fullWidth
          helperText={mail !== '' && emailError}
          onChange={event => {
            setMail(event.target.value);
          }}
          onKeyDown={async e => {
            e.stopPropagation(); // see : https://github.com/mui/material-ui/issues/19116
            if (e.key === 'Enter' && isEmailValid && !isSubmitting) {
              await handleSubmit();
            }
          }}
        />
        <Box mt={1} ml={0.5}>
          <Checkbox disabled={isSubmitting} size="small" label="Envoyer un email d'invitation" checked={shouldSendInvitationEmail} onChange={e => setShouldSendInvitationEmail(e.target.checked)} />
        </Box>
      </Grid>
      <Grid item xs={12} sm={2}>
        <Button disabled={!isEmailValid || isSubmitting} onClick={handleSubmit} color="primary" variant="contained">
          Ajouter
        </Button>
      </Grid>
    </Grid>
  );
};

AddUserForm.propTypes = {
  users: arrayOf(userShape).isRequired,
  addUser: func.isRequired,
  emailError: string.isRequired,
  setEmailError: func.isRequired,
  isSubmitting: bool.isRequired,
  formId: string.isRequired
};

export default AddUserForm;
